import { useEffect, useMemo, useState } from 'react'
import { Diagram } from '../Diagram'
import { useSelector } from 'react-redux'
import { selectedRangeSelector } from 'redux/dashboard/selectors'
import { dashboardApi } from 'api'
import { useApiRequest } from 'hooks/useApiRequest'
import { RevenuePerCarClassResponse } from 'api/dashboard/types'
import { diagramVariant } from '../../constants'
import { REVENUE_PER_CAR_DIAGRAM } from 'texts/dashboard'
import { supplierCurrencySelector } from 'redux/login/selectors'
import { RevenuePerCarProps } from './types'
import { CURRENCY_SYMBOL } from 'constants/currencies'
import { useCompanyId } from 'hooks/useCompanyId'

export const RevenuePerCar = ({ carCategories }: RevenuePerCarProps) => {
  const [items, setItems] = useState<RevenuePerCarClassResponse['items']>([])
  const selectedRange = useSelector(selectedRangeSelector)
  const supplierCurrency = useSelector(supplierCurrencySelector)

  const companyId = useCompanyId()

  const getRevenuePerCarClassRequest = useApiRequest(() =>
    dashboardApi.getRevenuePerCar({
      companyId: String(companyId),
      dateStart: selectedRange.from,
      dateEnd: selectedRange.to,
      carCategories,
    })
  )

  useEffect(() => {
    const getSchedule = async () => {
      const response = await getRevenuePerCarClassRequest.apiRequest()
      if (response) {
        setItems(response.data.items)
      }
    }
    getSchedule()
  }, [selectedRange.from, selectedRange.to, carCategories])

  const uniqueDates = [...new Set(items.map((item) => item.date))]

  const groupedData = useMemo(
    () =>
      carCategories?.map((type) => {
        const dataObj: Record<string, number> = {}
        uniqueDates.forEach((date) => {
          dataObj[date] = 0
        })

        items.forEach((item) => {
          if (item.carCategory === type) {
            dataObj[item.date] = item.revenue
          }
        })

        const dataArray = uniqueDates.map((date) => dataObj[date] || 0)

        return {
          name: type,
          data: dataArray,
        }
      }),
    [items]
  )

  const columnsCount = useMemo(
    () => groupedData?.reduce((acc, el) => acc + el.data.length, 0),
    [groupedData]
  )

  return (
    <Diagram
      series={groupedData}
      categories={uniqueDates}
      groupCount={groupedData?.length}
      columnsCount={columnsCount}
      currency={supplierCurrency}
      variant={diagramVariant.perCar}
      title={`${REVENUE_PER_CAR_DIAGRAM.TITLE}${
        supplierCurrency ? `, ${CURRENCY_SYMBOL[supplierCurrency]}` : ''
      }`}
      subtitle={REVENUE_PER_CAR_DIAGRAM.SUBTITLE}
      loading={getRevenuePerCarClassRequest.loading}
    />
  )
}
